import React from 'react';
import { Stack, StackItem } from '@domain-group/fe-blueprint-ui/layout';
import AverageRatingText from './average-star-rating-text';
import StarRatingDisplay from './star-rating-display';
import TotalReviewsText from './total-reviews-text';
import TooltipSearch from '../tooltip-search';
import * as styles from './average-star-rating-and-total.style';
import { COMPONENT_IDS } from '../../../constants';

export enum STAR_RATING_SECTION {
  HERO = 'hero',
  AGENT_CARDS = 'agentCards',
  AGENT_CARDS_NEUTRAL = 'agentCardsNeutral',
  AGENT_CARDS_DARK = 'agentCardsDark',
  RECOMMENDATIONS = 'recommendations',
  STICKY = 'sticky',
  SIDEBAR = 'sidebar',
  AGENCY_PROPERTY_SUMMARY = 'agencyPropertySummary',
  AGENCY_CARD = 'agencyCard',
  MOBILE = 'mobile',
}

type Props = {
  section: STAR_RATING_SECTION;
  averageRating?: number;
  totalReviews?: number;
  scrollTo?: boolean;
  onVendorTotalReviewsClick?: () => void;
  isSearch?: boolean;
};

interface SectionStyleConfig {
  starSize: number;
  verticalAlign: 'start' | 'center' | 'end';
  horizontalAlign: 'start' | 'center';
  vertical?: { compact: boolean; regular: boolean };
  fillStar?: string;
  starGap: number;
  strokeColor?: string;
  strokeWidth?: number;
  fillBackground?: string;
}

const getSectionStyles = (section: STAR_RATING_SECTION): SectionStyleConfig => {
  const stylesForSection = styles.starDimensions[section];
  const styleConfig: SectionStyleConfig = {
    starSize: stylesForSection.height,
    verticalAlign: 'start',
    horizontalAlign: 'start',
    starGap: 1,
    strokeColor: styles.gold,
    strokeWidth: 5,
    fillBackground: 'none',
  };

  switch (section) {
    case STAR_RATING_SECTION.HERO:
      styleConfig.verticalAlign = 'center';
      styleConfig.horizontalAlign = 'center';
      styleConfig.fillStar = styles.heroStickyColor;
      styleConfig.strokeColor = styles.heroStickyColor;
      break;
    case STAR_RATING_SECTION.RECOMMENDATIONS:
      styleConfig.verticalAlign = 'end';
      styleConfig.vertical = { compact: true, regular: false };
      styleConfig.starGap = 2;
      styleConfig.strokeColor = styles.gold;
      break;
    case STAR_RATING_SECTION.STICKY:
      styleConfig.fillStar = styles.heroStickyColor;
      styleConfig.strokeColor = styles.heroStickyColor;
      break;
    case STAR_RATING_SECTION.SIDEBAR:
      styleConfig.verticalAlign = 'center';
      styleConfig.horizontalAlign = 'center';
      styleConfig.fillStar = styles.heavyNeutralColor;
      styleConfig.strokeColor = styles.heavyNeutralColor;
      break;
    case STAR_RATING_SECTION.AGENT_CARDS_DARK:
      styleConfig.fillStar = styles.heavyNeutralColor;
      styleConfig.strokeColor = styles.heavyNeutralColor;
      break;
    case STAR_RATING_SECTION.MOBILE:
      styleConfig.fillStar = styles.heavyNeutralColor;
      styleConfig.strokeColor = styles.heavyNeutralColor;
      styleConfig.verticalAlign = 'start';
      styleConfig.horizontalAlign = 'center';
      styleConfig.vertical = { compact: true, regular: true };
      break;
    default:
      break;
  }

  return styleConfig;
};

const AverageStarRatingAndTotal = ({
  section,
  averageRating = 0,
  totalReviews = 0,
  scrollTo = false,
  onVendorTotalReviewsClick = () => undefined,
  isSearch = false,
}: Props): JSX.Element => {
  const {
    starSize,
    verticalAlign,
    horizontalAlign,
    vertical,
    fillStar,
    starGap,
    strokeColor,
    strokeWidth,
  } = getSectionStyles(section);

  const testIdPrefix = `${section}_`;

  const handleScroll = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    const targetElement = document.querySelector(
      `#${COMPONENT_IDS.recommendations}`,
    );
    if (targetElement) {
      const headerOffset = 50;
      const elementPosition =
        targetElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }

    if (onVendorTotalReviewsClick) {
      onVendorTotalReviewsClick();
    }
  };

  const isMobile = section === STAR_RATING_SECTION.MOBILE;

  return (
    <Stack
      horizontalAlign={horizontalAlign}
      verticalAlign={verticalAlign}
      vertical={vertical}
      gap={isMobile ? 'none' : 'xxs'}
      data-testid={`${testIdPrefix}star-rating`}
      id={`${testIdPrefix}star-rating`}
      css={styles.container(section)}
    >
      {isMobile ? (
        <>
          <StackItem css={styles.ratings}>
            <AverageRatingText
              value={averageRating}
              section={section}
              testIdPrefix={testIdPrefix}
            />
            <StarRatingDisplay
              rating={averageRating}
              fillStar={fillStar}
              starSize={starSize}
              starGap={starGap}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
              section={section}
              testIdPrefix={testIdPrefix}
            />
          </StackItem>
          <TotalReviewsText
            totalReviews={totalReviews}
            section={section}
            isMobile={isMobile}
            scrollTo={scrollTo}
            handleScroll={handleScroll}
            testIdPrefix={testIdPrefix}
          />
          {isSearch && <TooltipSearch />}
        </>
      ) : (
        <StackItem css={styles.ratings}>
          <AverageRatingText
            value={averageRating}
            section={section}
            testIdPrefix={testIdPrefix}
          />
          <StarRatingDisplay
            rating={averageRating}
            fillStar={fillStar}
            starSize={starSize}
            starGap={starGap}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
            section={section}
            testIdPrefix={testIdPrefix}
          />
          <TotalReviewsText
            totalReviews={totalReviews}
            section={section}
            isMobile={isMobile}
            scrollTo={scrollTo}
            handleScroll={handleScroll}
            testIdPrefix={testIdPrefix}
          />
          {isSearch && <TooltipSearch />}
        </StackItem>
      )}
    </Stack>
  );
};

export default AverageStarRatingAndTotal;
